import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import DocumentPreview from '../../../configuracion/plantillas/documentpreview';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

const ContractGenerationDialog = ({ open, onClose, prestamoId, idProductofinanciero }) => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [generatedContent, setGeneratedContent] = useState('');
  const [openPreview, setOpenPreview] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');

  // Fetch available templates
  const fetchPlantillas = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/plantillas/${localStorage.getItem('idcompany')}`, {
        headers: {
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Solo plantillas activas. Si tenemos el producto del préstamo, mostramos
        // las que coinciden con ese producto + las "universales" (sin producto).
        const activePlantillas = data.filter((plantilla) => {
          if (!plantilla.activo) return false;
          if (!idProductofinanciero) return true;
          return (
            !plantilla.id_productofinanciero ||
            plantilla.id_productofinanciero === idProductofinanciero
          );
        });
        setPlantillas(activePlantillas);
      } else {
        console.error('Error fetching plantillas');
        setMessageAlert('Error al cargar las plantillas');
        setAlertType('error');
        setOpenAlert(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessageAlert('Error al cargar las plantillas');
      setAlertType('error');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPlantillas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idProductofinanciero]);

  // Handle template selection
  const handleSelectTemplate = (plantilla) => {
    setSelectedPlantilla(plantilla);
  };

  // Generate document
  const handleGenerateDocument = async () => {
    if (!selectedPlantilla) {
      setMessageAlert('Por favor selecciona una plantilla');
      setAlertType('error');
      setOpenAlert(true);
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/plantillas/${selectedPlantilla.id}/generar/${prestamoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.ok) {
        const data = await response.json();

        setGeneratedContent(data.contenido_html);
        setOpenPreview(true);
        setMessageAlert('Documento generado exitosamente');
        setAlertType('success');
        setOpenAlert(true);
      } else {
        const error = await response.json();
        setMessageAlert(error.error || 'Error al generar el documento');
        setAlertType('error');
        setOpenAlert(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessageAlert('Error al generar el documento');
      setAlertType('error');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setSelectedPlantilla(null);
    setGeneratedContent('');
    setOpenPreview(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            height: '80vh',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #18586b 0%, #55b4ac 100%)',
            color: 'white',
            py: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DescriptionIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Generar Contrato de Préstamo
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                Selecciona una plantilla y genera el documento con datos reales
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: '#fafbfc', p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress size={60} sx={{ color: '#55b4ac' }} />
              <Typography variant="body1" sx={{ mt: 2, color: '#666' }}>
                Cargando plantillas...
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 3,
                pb: 2,
                borderBottom: '2px solid #e0e0e0'
              }}>
                <DescriptionIcon sx={{ color: '#55b4ac', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="600">
                  Plantillas Disponibles
                </Typography>
                <Chip 
                  label={`${plantillas.length} plantilla${plantillas.length !== 1 ? 's' : ''}`} 
                  size="small" 
                  color="white"
                  sx={{ 
                    backgroundColor: '#55b4ac',
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
              </Box>
              
              {plantillas.length === 0 ? (
                <Box sx={{ 
                  mt: 2, 
                  p: 3, 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: 2,
                  border: '1px solid #1976d2',
                  textAlign: 'center'
                }}>
                  <DescriptionIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                  <Typography variant="body1" color="#1976d2" fontWeight="600" gutterBottom>
                    No hay plantillas disponibles
                  </Typography>
                  <Typography variant="body2" color="#1976d2">
                    Crea una plantilla en Configuración → Plantillas.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ 
                  maxHeight: 400, 
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#55b4ac',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#18586b'
                    }
                  }
                }}>
                  {plantillas.map((plantilla, index) => (
                    <React.Fragment key={plantilla.id}>
                      <ListItem
                        button
                        onClick={() => handleSelectTemplate(plantilla)}
                        sx={{
                          backgroundColor: selectedPlantilla?.id === plantilla.id ? '#e0f2f1' : 'white',
                          border: selectedPlantilla?.id === plantilla.id ? '2px solid #55b4ac' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          mb: 2,
                          p: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: selectedPlantilla?.id === plantilla.id ? '#e0f2f1' : '#f5f7fa',
                            borderColor: '#55b4ac',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(85, 180, 172, 0.2)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box 
                            sx={{ 
                              backgroundColor: selectedPlantilla?.id === plantilla.id ? '#55b4ac' : '#e0e0e0',
                              borderRadius: 2,
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <DescriptionIcon 
                              sx={{ 
                                color: selectedPlantilla?.id === plantilla.id ? 'white' : '#666',
                                fontSize: 28
                              }} 
                            />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="h6" fontWeight="600">
                                {plantilla.nombre}
                              </Typography>
                              {selectedPlantilla?.id === plantilla.id && (
                                <CheckCircleIcon sx={{ color: '#55b4ac', fontSize: 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {plantilla.descripcion || 'Sin descripción'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={plantilla.tipo === 'contrato_prestamo' ? 'Contrato Préstamo' : plantilla.tipo}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                  }}
                                />
                                <Chip
                                  label={plantilla.producto_financiero_nombre || 'Universal'}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: plantilla.producto_financiero_nombre ? '#55b4ac' : '#9e9e9e',
                                    color: plantilla.producto_financiero_nombre ? '#18586b' : '#616161',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                  }}
                                />
                                <Chip
                                  label="Activo"
                                  size="small"
                                  color='white'
                                  sx={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </Box>
                            </Box>
                          }
                          sx={{
                            marginLeft: 2
                          }}
                        />
                      </ListItem>
                      {index < plantillas.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#fafbfc', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleClose} 
            startIcon={<CloseIcon />}
            variant="outlined"
            sx={{ 
              fontWeight: 600,
              px: 3
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGenerateDocument}
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={!selectedPlantilla || loading}
            color="primarybutton"
            size="large"
            sx={{ 
              fontWeight: 'bold',
              px: 4,
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#666'
              }
            }}
          >
            {loading ? 'Generando...' : 'Generar Documento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreview
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        plantilla={{
          nombre: selectedPlantilla?.nombre || 'Documento Generado',
          descripcion: selectedPlantilla?.descripcion || '',
          tipo: selectedPlantilla?.tipo || 'contrato_prestamo',
          activo: true,
          contenido_html: generatedContent
        }}
      />

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={messageAlert}
        severity={alertType}
        title=""
      />
    </>
  );
};

export default ContractGenerationDialog;
